import { useEffect, useState } from 'react'
import { buildStamp } from '../../shared/clock'
import { competitionRank } from '../../shared/rank'
import type { Team } from '../../shared/teams'
import { api, type ResolvedFavorites, type SeasonRow, type State } from '../api'
import { pushState, sendTest, subscribe, subscribedPlayers, unsubscribe, type PushState } from '../push'
import { Empty, WeekDates } from '../ui'

/*
 * Profile, built from turn 9a of the design canvas.
 *
 * Everything here is a player's own: his name, his PIN, his teams. It is the
 * only screen that writes to `me/*` rather than `admin/*`, and that difference
 * is the whole point - the commissioner can clear a forgotten PIN and has
 * never been able to choose one, because a PIN he chose would be a PIN he
 * knows.
 *
 * Nothing on this screen is urgent, so there is no mint on it. Gold appears
 * exactly twice: on the two favourite teams, and on the season rank. Both are
 * things this player did.
 *
 * The season snapshot is READ from the endpoint the Season table already
 * reads. It is not a second calculation of the same three numbers; two places
 * computing a rank is two places to disagree about a tie.
 */

/*
 * The design's actions are 36px tall and its toggle 34x20, both under the 44px
 * floor this app holds itself to. The box keeps the size it was drawn at and
 * an invisible overlay carries the touch target out to 44 in both directions.
 */
const TAP =
  "relative after:absolute after:top-1/2 after:left-1/2 after:h-11 after:w-full after:min-w-11 after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']"

/** RENAME / CHANGE. Slate, because none of them is something you did. */
const ACTION = `${TAP} slate-num inline-flex min-h-[38px] shrink-0 items-center justify-center rounded-[3px] border border-hairline px-3 text-[10px] font-semibold tracking-[0.08em] text-slate uppercase`

const CARD_LABEL = 'slate-num text-[9px] tracking-[0.1em] text-faint uppercase'

const FIELD =
  'slate-num h-11 w-full rounded-[3px] border border-hairline bg-ground px-2 text-[15px] text-ink focus:border-gold-edge focus:outline-none'

type Editing = null | 'name' | 'pin' | 'cfb' | 'nfl'

export function Profile({
  state,
  tz,
  onChanged,
}: {
  state: State
  tz: string
  onChanged: () => void
}) {
  const [season, setSeason] = useState<SeasonRow[] | null>(null)
  const [editing, setEditing] = useState<Editing>(null)
  const [draft, setDraft] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  /*
   * Null until the server has told us what the stored favourites RESOLVE to,
   * and null forever if it cannot reach ESPN. Both are honestly "we do not
   * know yet", and neither is allowed to look like "you have no team".
   */
  const [favs, setFavs] = useState<ResolvedFavorites | null>(null)

  useEffect(() => {
    void api.season().then((r) => setSeason(r.rows))
    void api.me.resolvedFavorites().then(setFavs, () => {})
  }, [])

  const me = state.players.find((p) => p.id === state.me.playerId)
  if (!me) return <Empty>Tap your name in the masthead first.</Empty>

  const open = (what: Exclude<Editing, null>, value: string) => {
    setMsg(null)
    setDraft(value)
    setEditing(what)
  }

  /*
   * One place where a write can fail, and it fails in WORDS.
   *
   * The server owns these rules - four digits, one to twenty characters, no
   * second person called Dad - so the message it returns is better than
   * anything guessed here. It is shown in slate rather than an alarm colour
   * because a rejected PIN is a fact about the form, not a fault.
   */
  const save = async (fn: () => Promise<unknown>) => {
    setMsg(null)
    try {
      await fn()
      setEditing(null)
      onChanged()
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'That did not work.')
    }
  }

  /*
   * A favourite is written and then READ BACK, not assumed.
   *
   * The row that was tapped came from ESPN a moment ago, so the write will
   * almost certainly stand - but "almost certainly" is exactly the confidence
   * that produced the original bug. What the card shows is what the server
   * says it resolved, every time.
   */
  const pick = (kind: 'cfb' | 'nfl', team: Team) =>
    void save(async () => {
      await api.me.favorites({ [kind]: team.full })
      setFavs(await api.me.resolvedFavorites())
    })

  const hasPin = me.has_pin === 1

  /* `rows` arrives sorted by total descending, so a place is a lookup. */
  const mine = season ? season.findIndex((r) => r.playerId === me.id) : -1
  const row = mine >= 0 ? season?.[mine] : undefined
  const rank =
    season && mine >= 0 ? (competitionRank(season.map((r) => r.total))[mine] ?? null) : null
  const best = row?.bestWeekId ? (row.byWeek[row.bestWeekId] ?? 0) : null

  const stamp = buildStamp(__BUILD_ID__, __BUILT_AT__, tz)

  return (
    <div className="pb-2">
      <header className="flex items-baseline justify-between gap-3 px-[18px] pt-5 pb-3.5">
        {/* 1.2, not leading-none: the design's header is 70px tall, which is
            20 + a 36px line box + 14. Big Shoulders' own normal leading runs
            to 45 and would push the first card nine pixels down. */}
        <span className="text-[30px] leading-[1.2] font-extrabold tracking-[0.03em] uppercase">
          Profile
        </span>
        <span className="text-right">
          <span className="slate-num block text-[10.5px] font-medium tracking-[0.1em] text-mute uppercase">
            Pick Six
          </span>
          <WeekDates week={state.week} />
        </span>
      </header>

      {/* --- who you are ---------------------------------------------------- */}
      <div className="slate-card mx-3 mb-2.5 flex items-center gap-2.5 p-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className={CARD_LABEL}>You play as</span>
          {editing === 'name' ? (
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void save(() => api.me.name(draft.trim()))}
              aria-label="Your name"
              autoFocus
              className={FIELD}
            />
          ) : (
            <span className="truncate text-[24px] leading-none font-bold tracking-[0.02em] uppercase">
              {me.name}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() =>
            editing === 'name' ? void save(() => api.me.name(draft.trim())) : open('name', me.name)
          }
          className={ACTION}
        >
          {editing === 'name' ? 'Save' : 'Rename'}
        </button>
      </div>

      {/* --- the PIN -------------------------------------------------------- */}
      <div className="slate-card mx-3 mb-2.5 flex items-center gap-2.5 p-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className={CARD_LABEL}>Pin</span>
          {editing === 'pin' ? (
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void save(() => api.me.pin(draft.trim()))}
              inputMode="numeric"
              maxLength={4}
              placeholder="Four digits"
              aria-label="Your new PIN"
              autoFocus
              className={`${FIELD} tracking-[0.3em]`}
            />
          ) : (
            <span className="slate-num text-[9.5px] leading-[1.5] text-mute">
              Asked once when you open your picks. Optional &mdash; turn it off any time.
              {/*
               * Admin follows the commissioner's NAME now, not his PIN, so the
               * PIN here is what it is for everybody else - a lock on picking
               * as you. Said plainly, because a shared phone is the whole
               * reason this app has a picker at all.
               */}
              {me?.role === 'commissioner' && (
                <span className="mt-1 block text-mute">
                  Admin follows your name, with or without this.
                </span>
              )}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={() =>
            editing === 'pin' ? void save(() => api.me.pin(draft.trim())) : open('pin', '')
          }
          className={ACTION}
        >
          {editing === 'pin' ? 'Save' : 'Change'}
        </button>

        <span className="flex shrink-0 items-center gap-1.5">
          <span
            className={[
              'slate-num text-[8.5px] font-medium tracking-[0.08em] uppercase',
              hasPin ? 'text-slate' : 'text-faint',
            ].join(' ')}
          >
            Pin
          </span>
          {/*
           * OFF clears the PIN outright. ON cannot do anything on its own -
           * there is nothing to turn on until four digits exist - so that
           * direction opens the field rather than pretending to be a setting.
           * Same one-way shape as the commissioner's roster toggle, for the
           * opposite reason.
           */}
          <Switch
            on={hasPin}
            label="You have a PIN"
            onToggle={() => (hasPin ? void save(() => api.me.pin(null)) : open('pin', ''))}
          />
        </span>
      </div>

      {/* --- favourite teams ------------------------------------------------ */}
      <div className="slate-card mx-3 mb-2.5 flex flex-col gap-3 p-3">
        <span className={CARD_LABEL}>Favorite teams — they get a ★ when the board is built</span>

        <Favorite
          kind="cfb"
          label="College"
          stored={me.favorite_cfb ?? null}
          resolved={favs?.cfb ?? null}
          editing={editing === 'cfb'}
          onOpen={() => open('cfb', '')}
          onClose={() => setEditing(null)}
          onPick={(t) => pick('cfb', t)}
        />
        <div className="h-px bg-hairline" />
        <Favorite
          kind="nfl"
          label="NFL"
          stored={me.favorite_nfl ?? null}
          resolved={favs?.nfl ?? null}
          editing={editing === 'nfl'}
          onOpen={() => open('nfl', '')}
          onClose={() => setEditing(null)}
          onPick={(t) => pick('nfl', t)}
        />
      </div>

      {/* --- the season, in three numbers ----------------------------------- */}
      <div className="slate-card mx-3 mb-2.5 flex flex-col gap-2.5 p-3">
        <span className={CARD_LABEL}>Your season</span>
        {/*
         * Recessed tiles, because these are things that HAPPENED rather than
         * things to press. Only the rank is gold: a place is something you
         * did, while a points total is a fact about the game.
         */}
        <div className="flex gap-1.5">
          <Tile value={rank} label="Rank" gold />
          <Tile value={row?.total ?? null} label="Points" />
          <Tile value={best} label="Best wk" />
        </div>
      </div>

      <Reminders state={state} />
      <Commissioner state={state} onChanged={onChanged} />

      {msg && <p className="px-[18px] pb-2 text-[13px] text-slate">{msg}</p>}

      {/*
       * The build, quietly, at the very bottom.
       *
       * Not a version number - nobody increments one here - but the SHA and
       * the moment it was built, which together answer the only question
       * anybody actually asks after a deploy: is this phone on the new one?
       * Small and mute because it is for the two minutes a year it is needed.
       */}
      {stamp && (
        <p className="slate-num px-[18px] pt-1 pb-3 text-[8.5px] tracking-[0.1em] text-mute">
          {stamp}
        </p>
      )}
    </div>
  )
}

/**
 * The way into Setup, and the only one.
 *
 * The ADMIN tab keys off a short-lived cookie that `POST /api/elevate` mints
 * against SETUP_PIN - and until this existed, nothing in the app ever called
 * it. The tab could therefore never appear, which made the whole of Admin
 * unreachable from inside the app it belongs to.
 *
 * Being the commissioner is what shows this card; the PIN is what grants the
 * authority. The role is a fact about a person and the PIN is a credential,
 * and the server has never confused the two - it checks the token on every
 * admin route regardless of who the client claims to be.
 *
 * It expires after thirty minutes, deliberately. A phone left on a counter
 * should not stay able to publish a board.
 */
function Commissioner({ state, onChanged }: { state: State; onChanged: () => void }) {
  const [pin, setPin] = useState('')
  const [msg, setMsg] = useState<string | null>(null)
  const me = state.players.find((p) => p.id === state.me.playerId)
  if (me?.role !== 'commissioner') return null

  if (state.me.isAdmin) {
    return (
      <div className="slate-card mx-3 mb-2.5 flex flex-col gap-1.5 p-3">
        <span className={CARD_LABEL}>Commissioner</span>
        <p className="text-[13px] text-slate">
          Setup is unlocked. The Admin tab is in the bar below, and it locks itself again
          after thirty minutes.
        </p>
      </div>
    )
  }

  return (
    <div className="slate-card mx-3 mb-2.5 flex flex-col gap-2.5 p-3">
      <span className={CARD_LABEL}>Commissioner</span>
      <p className="text-[13px] text-slate">
        Your setup PIN unlocks the Admin tab &mdash; the board, the roster and the rules.
      </p>
      <div className="flex items-center gap-2">
        <input
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          inputMode="numeric"
          maxLength={8}
          aria-label="Setup PIN"
          className="slate-num slate-tap w-28 rounded-[3px] border border-hairline bg-ground px-3 text-center text-[17px] tracking-[0.3em] focus:border-gold-edge focus:outline-none"
        />
        <button
          type="button"
          disabled={pin.length < 4}
          onClick={async () => {
            setMsg(null)
            try {
              await api.elevate(pin)
              setPin('')
              onChanged()
            } catch (e) {
              setMsg(e instanceof Error ? e.message : 'That did not work.')
            }
          }}
          className="slate-tap rounded-[3px] bg-ink px-4 text-[13px] font-bold tracking-[0.06em] text-card uppercase disabled:opacity-40"
        >
          Unlock
        </button>
      </div>
      {msg && <p className="text-[13px] text-slate">{msg}</p>}
    </div>
  )
}

/**
 * Notifications, for everyone who picks on THIS phone.
 *
 * The card used to be headed "Reminders on this phone", and the commissioner
 * went looking for push notifications, scrolled past it twice and reported
 * that the app did not have them. It is headed with the word he was scanning
 * for now. The card was never missing; its name was.
 *
 * A subscription belongs to a device, not to a person, and this device carries
 * several people - the eight-year-old picks on Dad's. Subscribing only the
 * player who happens to be selected means the two people most likely to need
 * "you have four games left" are the two who can never receive it.
 *
 * On iOS push exists only for a PWA added to the home screen; in Safari the
 * permission prompt is not implemented at all. Saying so is an instruction,
 * where a button that silently does nothing is a dead end - and is why people
 * believe web push does not work on an iPhone.
 */
function Reminders({ state }: { state: State }) {
  const [ps, setPs] = useState<PushState | null>(null)
  const [on, setOn] = useState<string[]>([])
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void pushState().then(setPs)
    void subscribedPlayers().then(setOn)
  }, [])

  if (ps === null || ps === 'unsupported') return null

  const save = async (ids: string[]) => {
    setBusy(true)
    setMsg(null)
    try {
      if (ids.length === 0) {
        setPs(await unsubscribe())
        setOn([])
      } else {
        setPs(await subscribe(ids))
        setOn(ids)
      }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Could not change that.')
    } finally {
      setBusy(false)
    }
  }

  const toggle = (id: string) =>
    void save(on.includes(id) ? on.filter((x) => x !== id) : [...on, id])

  return (
    <div className="slate-card mx-3 mb-2.5 flex flex-col gap-2.5 p-3">
      <span className={CARD_LABEL}>Notifications on this phone</span>

      {ps === 'needs-install' ? (
        <p className="text-[13px] text-slate">
          Add Pick Six to your home screen first &mdash; on iPhone, notifications only
          work once it is installed.
        </p>
      ) : ps === 'blocked' ? (
        <p className="text-[13px] text-slate">
          Notifications are switched off for this site in your browser settings.
        </p>
      ) : (
        <>
          {/*
            * The tick list arrived with no statement of what ticking DOES,
            * which is the same failure as the card's old name: the control
            * was there and nothing said so. Notifications go to the phone,
            * not to an account, so the sentence has to carry both halves -
            * what a tick turns on, and why it is a list rather than a switch.
            */}
          <p className="text-[13px] leading-[1.45] text-slate">
            Tick a name to turn notifications on for that person &mdash; kickoff reminders and
            new boards. Notifications belong to this phone, so tick everyone who picks on it.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {state.players.map((p) => {
              const ticked = on.includes(p.id)
              return (
                <button
                  key={p.id}
                  type="button"
                  disabled={busy}
                  onClick={() => toggle(p.id)}
                  aria-pressed={ticked}
                  className={[
                    'slate-tap rounded-[3px] px-3 text-[13px] font-bold tracking-[0.02em] uppercase',
                    ticked
                      ? 'bg-gold text-nightink'
                      : 'border border-hairline bg-card text-slate',
                  ].join(' ')}
                >
                  {p.name}
                </button>
              )
            })}
          </div>

          {on.length > 0 && (
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true)
                const r = await sendTest()
                setMsg(r.detail)
                setBusy(false)
              }}
              className="slate-tap rounded-[3px] border border-hairline px-3 text-[12px] font-bold tracking-[0.06em] text-slate uppercase"
            >
              Send a test notification
            </button>
          )}
        </>
      )}

      {msg && <p className="text-[13px] text-slate">{msg}</p>}
    </div>
  )
}

/** 34x20, square-cornered, knob right when on. The app's only switch shape. */
function Switch({ on, label, onToggle }: { on: boolean; label: string; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={[
        TAP,
        'block h-5 w-[34px] shrink-0 rounded-[3px]',
        on ? 'bg-ink' : 'border border-hairline bg-recessed',
      ].join(' ')}
    >
      <span
        className={[
          'pointer-events-none absolute block h-3.5 w-3.5 rounded-[2px]',
          on ? 'top-[3px] right-[3px] bg-ground' : 'top-[2px] left-[2px] border border-hairline bg-card',
        ].join(' ')}
      />
    </button>
  )
}

/**
 * One favourite team: typed at, but never typed IN.
 *
 * A favourite is stored as ESPN's `displayName` because that is the only
 * string anything downstream matches - `softFavorites.has(g.home.full)` in the
 * board builder, string equality for Add Game's FAMILY TEAMS group. The
 * free-text box this replaces accepted "BYU" and "49ERS", saved them, matched
 * nothing for a season and never said a word about it. So what is typed here
 * is a QUERY; the value only ever comes off a row ESPN named.
 *
 * The typed text is deliberately NOT the profile's shared `draft`. A draft is
 * a value on its way to being saved, and this one is on its way to being
 * thrown away.
 */
function Favorite({
  kind,
  label,
  stored,
  resolved,
  editing,
  onOpen,
  onClose,
  onPick,
}: {
  kind: 'cfb' | 'nfl'
  label: string
  /** What is in the column, whatever it happens to say. */
  stored: string | null
  /** What the server made of it. Null while unknown - see `favs` in Profile. */
  resolved: { stored: string | null; team: Team | null } | null
  editing: boolean
  onOpen: () => void
  onClose: () => void
  onPick: (team: Team) => void
}) {
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<Team[]>([])
  const [note, setNote] = useState<string | null>(null)

  /* A fresh search each time the row opens; last week's query is not a start. */
  useEffect(() => {
    if (editing) {
      setQ('')
      setHits([])
      setNote(null)
    }
  }, [editing])

  /*
   * Add Game's debounce, at Add Game's 220ms, for Add Game's reason: the list
   * is the server's, so a keystroke is a request. An empty query returns
   * nothing rather than 138 college teams - see `matchTeams`.
   */
  useEffect(() => {
    if (!editing) return
    const t = window.setTimeout(async () => {
      try {
        setHits((await api.teams(kind, q)).teams)
      } catch {
        setNote('Could not reach the team list just now.')
      }
    }, q ? 220 : 0)
    return () => window.clearTimeout(t)
  }, [editing, kind, q])

  /*
   * Three things it can say, and the third is the whole point of this screen.
   *
   * A stored value the server could not resolve is shown as NOT SET, because
   * that is what it is: the board will never match it, so the star it implies
   * will never appear. Printing it as though it worked is the silent failure
   * being fixed.
   */
  const unmatched = resolved !== null && resolved.stored !== null && resolved.team === null
  const shown = resolved?.team?.name ?? (unmatched ? null : stored)

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2.5">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="slate-num text-[8.5px] font-medium tracking-[0.08em] text-gold-text uppercase">
            ★ {label}
          </span>
          {editing ? (
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={kind === 'cfb' ? 'Type a few letters — byu, uga' : 'Type a few letters — sf, chiefs'}
              aria-label={`Search ${label} teams`}
              autoComplete="off"
              autoFocus
              className={FIELD}
            />
          ) : (
            <span
              className={[
                'truncate text-[20px] leading-none font-bold tracking-[0.02em] uppercase',
                shown ? '' : 'text-faint',
              ].join(' ')}
            >
              {shown ?? 'Not set'}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => (editing ? onClose() : onOpen())}
          className={ACTION}
        >
          {editing ? 'Cancel' : 'Change'}
        </button>
      </div>

      {/*
        * Naming the value it is refusing, rather than just going blank. The
        * commissioner typed "BYU" a year ago; "Not set" alone would read as
        * the app having lost it.
        */}
      {!editing && unmatched && (
        <p className="text-[13px] leading-[1.45] text-slate">
          &ldquo;{resolved?.stored}&rdquo; is not a team ESPN lists, so it has never starred a
          game. Tap Change and pick it from the list.
        </p>
      )}

      {editing && (
        <div className="flex flex-col gap-1.5">
          {hits.map((t) => (
            <button
              key={t.full}
              type="button"
              onClick={() => onPick(t)}
              className="slate-tap flex items-center gap-2 rounded-[3px] border border-hairline bg-ground px-2.5 text-left"
            >
              <span className="min-w-0 flex-1 truncate text-[15px] font-bold tracking-[0.02em] text-ink uppercase">
                {t.full}
              </span>
              <span className="slate-num shrink-0 text-[10px] font-semibold tracking-[0.08em] text-mute">
                {t.abbr}
              </span>
            </button>
          ))}
          {note ? (
            <p className="text-[13px] text-slate">{note}</p>
          ) : (
            <p className="slate-num text-[9px] tracking-[0.08em] text-mute uppercase">
              {q && hits.length === 0 ? 'No team matches that' : 'Tap a team to save it'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/** RANK / POINTS / BEST WK. An em-dash until the season endpoint answers. */
function Tile({ value, label, gold }: { value: number | null; label: string; gold?: boolean }) {
  return (
    <div className="flex flex-1 flex-col items-center gap-0.5 rounded-[4px] bg-ground px-1.5 py-2.5">
      <span
        className={
          gold
            ? /* Big Shoulders, not mono: a place is a headline, not a column of
                 digits to be compared straight down. */
              'text-[24px] leading-none font-extrabold text-gold-text'
            : 'slate-num text-[24px] leading-none font-bold'
        }
      >
        {value ?? '—'}
      </span>
      <span className="slate-num text-[8.5px] tracking-[0.08em] text-mute uppercase">{label}</span>
    </div>
  )
}
